import React, { FC, useEffect, useRef, useState } from 'react';
import { LoginModalSchema } from './Login.types';
import Login from './LoginV2';
import Styles from './ReusableLogin.module.scss';
import DepricatingChannels from './MfaPhase2/DepricatingChannels';
import NewNumber from './MfaPhase2/NewNumber';
import OtpValidation from './MfaPhase2/OtpValidation';
import ResetPwdModal from './MfaPhase2/ResetPwdModal';
import {
  DEPRICATED_EMAIL,
  DEPRICATING_EMAIL,
  DEPRICATING_SMS,
  LOGIN_MFA,
  LOGIN_OTP,
  OTP_EMAIL,
  OTP_NR_LIST,
  OTP_NR_NEW,
  OTP_SMS,
  OTP_SMS_NEW,
  OTP_SMS_OLD,
  PW_CHANGED,
  PW_FORGOT,
  PW_RESET
} from '../../utils/constants';
import { MyPolicyCallByAction, fetchAndUpdateChatToken } from '../../services/LoginServiceV2';

const LoginModal: FC<LoginModalSchema> = ({
  content,
  closeLogin,
  metadata,
  onSuccess,
  isProdEnv = true
}): any => {
  const [loginContent, setLoginContent] = useState<any>(content);
  const [currentView, setCurrentView] = useState<string>(LOGIN_MFA);
  const [userID, setUserID] = useState<string>('');
  const [assests, setAssests] = useState<any>(content);
  const [loginResponseMFA, setLoginResponseMFA] = useState<any>('');
  const [password, setPassword] = useState<any>('');
  const [mfaContentV2, setMfaContentV2] = useState<any>(content);
  const [forgotPwdContent, setForgotPwdContent] = useState<any>(content);
  const [resetPwdContent, setResetPwdContent] = useState<any>(content);
  const [resetPwdSuccessContent, setResetPwdSuccessContent] = useState<any>(content);
  const controllerRef = useRef<AbortController | null>(null);

  const handleSuccess = () => {
    fetchAndUpdateChatToken();
    onSuccess();
  };

  useEffect(() => {
    if (content) {
      if (content?.reference?.content?.component === 'loginPageTemplate') {
        setLoginContent(content?.reference?.content?.body?.[0]?.popup?.[0]);
        setAssests(content?.reference?.content?.body?.[0]?.icons?.[0]);
        setMfaContentV2(content?.reference?.content?.body?.[0]?.mfaV2?.[0]);
        setForgotPwdContent(content?.reference?.content?.body?.[0]?.forgotPwd?.[0]);
        setResetPwdContent(content?.reference?.content?.body?.[0]?.resetPwd?.[0]);
        setResetPwdSuccessContent(content?.reference?.content?.body?.[0]?.resetPwdSuccess?.[0]);
      } else {
        setLoginContent(content?.popup?.[0]);
        setAssests(content?.icons?.[0]);
        setMfaContentV2(content?.mfaV2?.[0]);
        setForgotPwdContent(content?.forgotPwd?.[0]);
        setResetPwdContent(content?.resetPwd?.[0]);
        setResetPwdSuccessContent(content?.resetPwdSuccess?.[0]);
      }
    }
  }, [content]);

  const changeCurrentView = (userId: string, response?: any, pswd?: string): void => {
    if (userId !== '') {
      setUserID(userId);
    }
    setCurrentView(response?.status);
    setLoginResponseMFA(response);
    setPassword(pswd);
  };

  const backButton = (): void => {
    controllerRef?.current?.abort();
    MyPolicyCallByAction('BACK', '', controllerRef)
      .then((res: any) => {
        if (res?.status && res?.status !== 'NOK' && res?.status !== 'ERROR') {
          changeCurrentView('', res);
        } else {
          changeCurrentView(userID, { status: LOGIN_MFA }, password);
        }
      })
      .catch(() => changeCurrentView(userID, { status: LOGIN_MFA }));
  };

  //Function to load screens based on response status code
  const innerData = (): any => {
    switch (currentView) {
      case LOGIN_MFA:
        return (
          <Login
            content={loginContent}
            changeCurrentView={changeCurrentView}
            metadata={metadata}
            onSuccess={handleSuccess}
            loginIcons={assests}
            userID={userID}
            password={password}
            isProdEnv={isProdEnv}
          />
        );
      case DEPRICATING_SMS:
      case DEPRICATING_EMAIL:
      case DEPRICATED_EMAIL:
      case OTP_NR_LIST:
      case PW_CHANGED:
        return (
          <DepricatingChannels
            useCase={currentView}
            mainContent={mfaContentV2 ?? {}}
            changeCurrentView={changeCurrentView}
            response={loginResponseMFA}
            resetPwdSuccessContent={resetPwdSuccessContent}
            loginIcons={assests}
            errorContent={loginContent}
          />
        );
      case OTP_SMS_OLD:
      case OTP_SMS_NEW:
      case OTP_SMS:
      case OTP_EMAIL:
        return (
          <OtpValidation
            useCase={currentView}
            mainContent={mfaContentV2 ?? {}}
            changeCurrentView={changeCurrentView}
            response={loginResponseMFA}
            loginIcons={assests}
            onSuccess={handleSuccess}
            errorContent={loginContent}
          />
        );
      case OTP_NR_NEW:
        return (
          <NewNumber
            useCase={currentView}
            content={mfaContentV2?.newNumberSection?.[0] ?? {}}
            changeCurrentView={changeCurrentView}
            loginIcons={assests}
            response={loginResponseMFA}
            errorContent={loginContent}
          />
        );
      case LOGIN_OTP:
      case PW_FORGOT:
        return (
          <NewNumber
            useCase={currentView}
            content={forgotPwdContent ?? {}}
            changeCurrentView={changeCurrentView}
            metadata={metadata}
            loginIcons={assests}
            userID={userID}
            errorContent={loginContent}
          />
        );
      case PW_RESET:
        return (
          <ResetPwdModal
            content={resetPwdContent}
            useCase={currentView}
            loginIcons={assests}
            changeCurrentView={changeCurrentView}
            errorContent={loginContent}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <div className={Styles.loginmodal}>
      <div className='w-100 h-100 d-flex position-relative justify-content-center align-items-center'>
        <div className={`${Styles['login-container']}`}>
          <div className='position-relative w-100' role='button'>
            {/* Hiding back button in case of LOGIN_MFA and PW_CHANGED */}
            {currentView !== LOGIN_MFA && currentView !== PW_CHANGED && (
              <span style={{ display: 'flex', width: 'fit-content' }} onClick={backButton}>
                <img
                  src={assests?.back?.filename}
                  className={`${Styles['icon-container']}`}
                  alt='back'
                />
                <div className={`${Styles['back-text']}`}>{loginContent?.backTxt}</div>
              </span>
            )}
            {/* close icon */}
            <img
              src={assests?.close?.filename}
              className={`${Styles['icon-container']} position-absolute end-0 top-0`}
              alt='close'
              onClick={closeLogin}
            />
          </div>
          <div className='w-100 d-flex align-items-center flex-column'>{innerData()}</div>
        </div>
      </div>
    </div>
  );
};
export default LoginModal;
